<template>
  <HrPageLayout
    title="County Attorney Documents"
    subtitle="Sharable legal documents: Bills, Acts, Policies, Contracts, MoU"
    :breadcrumb-items="[{ label: 'County Attorney' }]"
  >
    <template #header-actions>
      <div class="hr-flex hr-gap-2">
        <button class="hr-btn hr-btn-primary" @click="openAddContract">Add Contract</button>
        <button class="hr-btn hr-btn-success" @click="openAddLegalDoc">Add Bills/Acts/Policies/MoU</button>
      </div>
    </template>
    <div class="hr-chart-card">
      <div class="hr-chart-header">
        <div class="hr-flex hr-gap-2">
          <select class="hr-select" v-model="docTypeFilter" @change="loadDocs">
            <option value="">All types</option>
            <option v-for="t in types" :key="t" :value="t">{{ t }}</option>
          </select>
        </div>
      </div>
      <div class="hr-chart-body">
        <div class="hr-table-wrapper">
          <table class="hr-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Title</th>
                <th>Date Added</th>
                <th>Contract</th>
                <th>Status</th>
                <th>Attachment</th>
                <th>Shared</th>
                <th>Sign In/Out</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="d in docs" :key="d.id">
                <td>{{ d.doc_type }}</td>
                <td>{{ d.title }}</td>
                <td>{{ d.created_at }}</td>
                <td>{{ d.contract_name || '—' }} {{ d.contract_value ? '(' + d.contract_value + ')' : '' }}</td>
                <td>{{ resolveStatus(d) }}</td>
                <td>
                  <button v-if="d.file_path" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="openAttachment(d)">View</button>
                  <span v-else>—</span>
                </td>
                <td>Ack {{ Number(d.ack_count || 0) }} of {{ Number(d.shared_count || 0) }}</td>
                <td>{{ Number(d.sign_in) ? 'In' : '—' }} / {{ Number(d.sign_out) ? 'Out' : '—' }}</td>
                <td>
                  <button class="hr-btn hr-btn-sm hr-btn-info" @click="editDoc(d)">Edit</button>
                  <button v-if="d.doc_type === 'MoU'" class="hr-btn hr-btn-sm hr-btn-warning" @click="changeMouStatus(d)">Change Status</button>
                  <button v-if="d.doc_type === 'Contracts' && !Number(d.sign_out)" class="hr-btn hr-btn-sm hr-btn-success" @click="markContractSent(d)">Mark Sign Out</button>
                  <button
                    class="hr-btn hr-btn-sm"
                    :class="Number(d.shared_count || 0) > 0 ? 'hr-btn-success' : 'hr-btn-primary'"
                    @click="openShareModal(d)"
                  >
                    {{ Number(d.shared_count || 0) > 0 ? 'Shared' : 'Share' }}
                  </button>
                  <button class="hr-btn hr-btn-sm hr-btn-secondary" @click="openAckModal(d)">View Acks</button>
                </td>
              </tr>
              <tr v-if="!docs.length"><td colspan="9" class="hr-text-center">No records</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <ModalWrapper ref="formModal" :title="form.id ? 'Edit Document' : (isContractForm ? 'Add Contract' : 'Add Legal Document')" size="large">
      <template #body>
        <form @submit.prevent class="hr-form-grid">
          <div>
            <label class="hr-form-label">Type</label>
            <select class="hr-select" v-model="form.doc_type">
              <option v-for="t in availableFormTypes" :key="'tt'+t" :value="t">{{ t }}</option>
            </select>
          </div>
          <div><label class="hr-form-label">Title</label><input class="hr-input" v-model="form.title"></div>
          <div v-if="isContractType"><label class="hr-form-label">Contract Name</label><input class="hr-input" v-model="form.contract_name"></div>
          <div v-if="isContractType"><label class="hr-form-label">Contract Value</label><input type="number" class="hr-input" v-model="form.contract_value"></div>
          <div v-if="isContractType"><label><input type="checkbox" v-model="form.sign_in"> Sign In</label></div>
          <div v-if="isContractType"><label><input type="checkbox" v-model="form.sign_out"> Sign Out</label></div>
          <div class="span2"><label class="hr-form-label">Notes</label><textarea class="hr-textarea" rows="2" v-model="form.notes"></textarea></div>
          <div class="span2">
            <label class="hr-form-label">Attachment</label>
            <div v-if="form.existing_file_path" class="hr-mb-1">
              Current:
              <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="openAttachment({ id: form.id, file_path: form.existing_file_path })">
                View current file
              </button>
            </div>
            <div v-if="form.existing_file_path" class="hr-mb-1">
              <label><input type="checkbox" v-model="form.remove_existing_file"> Remove current file</label>
            </div>
            <input ref="fileInput" type="file" accept=".pdf,.zip,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" class="hr-input" @change="onFile">
            <small class="text-muted">PDF, Word, Excel, ZIP, PNG, or JPG. Choose a new file to replace the current one.</small>
          </div>
        </form>
      </template>
      <template #footer>
        <button class="hr-btn hr-btn-secondary" @click="$refs.formModal.close()">Cancel</button>
        <button class="hr-btn hr-btn-success" @click="saveDoc">Save</button>
      </template>
    </ModalWrapper>

    <ModalWrapper ref="ackModal" title="Acknowledgements" size="large">
      <template #body>
        <div class="hr-table-wrapper">
          <table class="hr-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Shared At</th>
                <th>Acknowledged</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="a in ackItems" :key="`ack-${a.employee_id}-${a.shared_at}`">
                <td>{{ ackLabel(a) }}</td>
                <td>{{ a.shared_at || '—' }}</td>
                <td>{{ a.acknowledged_at || 'Pending' }}</td>
              </tr>
              <tr v-if="!ackItems.length"><td colspan="3" class="hr-text-center">No acknowledgements yet</td></tr>
            </tbody>
          </table>
        </div>
      </template>
      <template #footer>
        <button class="hr-btn hr-btn-secondary" @click="$refs.ackModal.close()">Close</button>
      </template>
    </ModalWrapper>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import ModalWrapper from "@/components/hrActions/ModalWrapper.vue";
import { execute, executeDownload, authUrl, toast } from "@/api";

export default {
  name: "CountyAttorneyDocuments",
  components: { HrPageLayout, ModalWrapper },
  computed: {
    isContractType() {
      return String(this.form?.doc_type || "") === "Contracts";
    },
    availableFormTypes() {
      return this.isContractForm ? ["Contracts"] : ["Bills", "Acts", "Policies", "MoU"];
    }
  },
  data() {
    return {
      types: ["Bills", "Acts", "Policies", "Contracts", "MoU"],
      docTypeFilter: "",
      docs: [],
      employees: [],
      ackItems: [],
      isContractForm: false,
      form: { id: null, doc_type: "Bills", title: "", notes: "", contract_name: "", contract_value: "", sign_in: false, sign_out: false, mou_status: "non_executed", approval_status: "pending", file: null, existing_file_path: "" }
    };
  },
  mounted() { this.loadDocs(); this.loadEmployees(); },
  methods: {
    resolveStatus(d) {
      if (Number(d?.shared_count || 0) > 0) return "shared";
      if ((d?.status || "").trim() !== "") {
        return String(d.status).trim().toLowerCase() === "share" ? "shared" : d.status;
      }
      if (d?.doc_type === "MoU") return d?.mou_status === "executed" ? "executed" : "not_executed";
      if (d?.doc_type === "Contracts") return Number(d?.sign_out) ? "sent" : "draft";
      return "active";
    },
    employeeLabel(e) {
      const name = [e.firstName, e.middleName, e.lastName].filter(Boolean).join(" ").trim();
      return `${name || "Employee"}${e.empNo ? ` (${e.empNo})` : ""}`;
    },
    ackLabel(a) {
      const name = [a.firstName, a.middleName, a.lastName].filter(Boolean).join(" ").trim();
      return `${name || "Employee"}${a.empNo ? ` (${a.empNo})` : ""}${a.nationalId ? ` - ${a.nationalId}` : ""}`;
    },
    async loadDocs() {
      const fd = new FormData();
      fd.append("function", "getCountyAttorneyDocuments");
      if (this.docTypeFilter) fd.append("docType", this.docTypeFilter);
      const res = await execute(fd, authUrl);
      this.docs = res?.data?.data?.documents || [];
    },
    async loadEmployees() {
      const fd = new FormData();
      fd.append("function", "getEmployees");
      const res = await execute(fd, authUrl);
      this.employees = res?.data?.data?.employees || res?.data?.data || [];
    },
    openAddContract() {
      this.isContractForm = true;
      this.form = { id: null, doc_type: "Contracts", title: "", notes: "", contract_name: "", contract_value: "", sign_in: false, sign_out: false, mou_status: "non_executed", approval_status: "pending", file: null, existing_file_path: "", remove_existing_file: false };
      this.$nextTick(() => { if (this.$refs.fileInput) this.$refs.fileInput.value = ""; });
      this.$refs.formModal.open();
    },
    openAddLegalDoc() {
      this.isContractForm = false;
      this.form = { id: null, doc_type: "Bills", title: "", notes: "", contract_name: "", contract_value: "", sign_in: false, sign_out: false, mou_status: "non_executed", approval_status: "pending", file: null, existing_file_path: "", remove_existing_file: false };
      this.$nextTick(() => { if (this.$refs.fileInput) this.$refs.fileInput.value = ""; });
      this.$refs.formModal.open();
    },
    editDoc(d) {
      this.isContractForm = String(d.doc_type || "") === "Contracts";
      this.form = { ...d, file: null, existing_file_path: d.file_path || "", remove_existing_file: false, sign_in: Number(d.sign_in) === 1, sign_out: Number(d.sign_out) === 1 };
      this.$nextTick(() => { if (this.$refs.fileInput) this.$refs.fileInput.value = ""; });
      this.$refs.formModal.open();
    },
    onFile(e) {
      this.form.file = e?.target?.files?.[0] || null;
      if (this.form.file) this.form.remove_existing_file = false;
    },
    async saveDoc() {
      if (!this.form.doc_type || !this.form.title) return toast.error("Type and title are required");
      if (!this.isContractType) {
        this.form.contract_name = "";
        this.form.contract_value = "";
        this.form.sign_in = false;
        this.form.sign_out = false;
      }
      const fd = new FormData();
      fd.append("function", "saveCountyAttorneyDocument");
      Object.keys(this.form).forEach((k) => {
        if (k === "file") return;
        if (k === "sign_in" || k === "sign_out" || k === "remove_existing_file") fd.append(k, this.form[k] ? "1" : "0");
        else fd.append(k, this.form[k] == null ? "" : String(this.form[k]));
      });
      if (this.form.file) fd.append("file", this.form.file);
      const res = await execute(fd, authUrl);
      if (res?.data?.success) {
        this.$refs.formModal.close();
        await this.loadDocs();
        toast.success("Saved");
      } else {
        toast.error(res?.data?.message || "Save failed");
      }
    },
    async changeMouStatus(d) {
      const current = this.resolveStatus(d) === "executed" ? "executed" : "not_executed";
      const next = current === "executed" ? "not_executed" : "executed";
      const fd = new FormData();
      fd.append("function", "changeCountyAttorneyStatus");
      fd.append("id", String(d.id));
      fd.append("status", next);
      const res = await execute(fd, authUrl);
      if (res?.data?.success) {
        await this.loadDocs();
        toast.success(`Status changed to ${next}`);
      } else {
        toast.error(res?.data?.message || "Status update failed");
      }
    },
    async markContractSent(d) {
      const fd = new FormData();
      fd.append("function", "markCountyAttorneyContractSignOut");
      fd.append("id", String(d.id));
      const res = await execute(fd, authUrl);
      if (res?.data?.success) {
        await this.loadDocs();
        toast.success("Marked as sent");
      } else {
        toast.error(res?.data?.message || "Update failed");
      }
    },
    async openShareModal(d) {
      if (!d?.id) return toast.error("No selected document");
      try {
        const fd = new FormData();
        fd.append("function", "shareCountyAttorneyDocument");
        fd.append("documentId", String(d.id));
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          const cnt = Number(res?.data?.data?.shared || 0);
          // Immediate UI update (no manual refresh needed)
          d.shared_count = cnt > 0 ? cnt : Number(d.shared_count || 0);
          d.status = "shared";
          toast.success(cnt > 0 ? `Shared to ${cnt} employee(s)` : "Shared successfully");
          // Keep background refresh for consistency, but UI is already updated above.
          this.loadDocs();
          this.$router.push({ name: "documents-from-attorney" });
        } else {
          toast.error(res?.data?.message || "Share failed");
        }
      } catch (e) {
        toast.error("Share failed. Please try again.");
      }
    },
    async openAckModal(d) {
      const fd = new FormData();
      fd.append("function", "getCountyAttorneyAcknowledgements");
      fd.append("documentId", String(d.id));
      const res = await execute(fd, authUrl);
      this.ackItems = res?.data?.data?.items || [];
      this.$refs.ackModal.open();
    },
    async openAttachment(d) {
      if (!d?.id) return;
      try {
        const fd = new FormData();
        fd.append("function", "downloadCountyAttorneyDocument");
        fd.append("id", String(d.id));
        const res = await executeDownload(fd, authUrl);
        const ct = String((res.headers && (res.headers["content-type"] || res.headers["Content-Type"])) || "");
        const blob = res?.data instanceof Blob
          ? res.data
          : new Blob([res?.data || ""], { type: ct || "application/octet-stream" });
        if (ct.indexOf("application/json") !== -1 || (blob.type && blob.type.indexOf("json") !== -1 && blob.size < 4096)) {
          const raw = await blob.text();
          let msg = "Could not open attachment";
          try {
            const j = JSON.parse(raw);
            if (j.message) msg = j.message;
          } catch (_) {}
          toast.error(msg);
          return;
        }
        if (blob.size < 800) {
          try {
            const t = await blob.slice(0, 800).text();
            if (t && t.trim().startsWith("{") && t.includes('"success"')) {
              const j = JSON.parse(t);
              if (j.success === false) {
                toast.error(j.message || "Could not open attachment");
                return;
              }
            }
          } catch (_) { /* binary ok */ }
        }
        const url = URL.createObjectURL(blob);
        const opened = window.open(url, "_blank");
        if (!opened) {
          const a = document.createElement("a");
          a.href = url;
          a.download = String(d.title || "document").replace(/[^\w.\- ]+/g, "_");
          document.body.appendChild(a);
          a.click();
          a.remove();
          toast.info("Pop-up blocked — file downloaded instead");
        }
        setTimeout(() => URL.revokeObjectURL(url), 120000);
      } catch (e) {
        toast.error(e?.message || "Could not open attachment");
      }
    }
  }
};
</script>

<style scoped>
.hr-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.span2 { grid-column: 1 / -1; }
</style>
